import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  Typography,
  TextField,
  InputAdornment,
  TableSortLabel,
  Collapse,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
  Person,
  Email,
  Phone,
  WhatsApp,
  CheckCircle,
  PendingActions,
  Visibility,
  Send,
  Download,
  Instagram,
  ContactEmergency,
  Restaurant,
  Schedule,
  Message,
  History,
} from '@mui/icons-material';
import { useGuestDetails, useSendGuestReminder, useExportGuestData, GuestDetail } from '@/hooks/api/useGuests';
import { Booking } from '@/types';
import { DocumentThumbnail, DocumentViewerModal } from '@/components/common';

interface GuestDetailsPanelProps {
  booking: Booking;
  listingTitle?: string;
}

type SortField = 'guestName' | 'checkInStatus' | 'completedAt' | 'documentsVerified';
type SortDirection = 'asc' | 'desc';

export const GuestDetailsPanel: React.FC<GuestDetailsPanelProps> = ({
  booking,
  listingTitle,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('guestName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<'id_front' | 'id_back'>('id_front');
  const [selectedGuestName, setSelectedGuestName] = useState<string>('');

  // API hooks
  const { data: guestDetailsData, isLoading, error } = useGuestDetails(booking.bookingId);
  const sendReminderMutation = useSendGuestReminder();
  const exportDataMutation = useExportGuestData();

  const guests = guestDetailsData?.guests || [];
  const hasAccessDenied = guestDetailsData?.accessDenied || false;

  // Filter and sort guests
  const filteredGuests = guests
    .filter(guest =>
      !searchTerm ||
      guest.guestName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.contactInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.contactInfo?.whatsappNumber?.includes(searchTerm)
    )
    .sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle special cases
      if (sortField === 'completedAt') {
        aValue = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        bValue = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      } else if (sortField === 'documentsVerified') {
        aValue = a.documentsVerified ? 1 : 0;
        bValue = b.documentsVerified ? 1 : 0;
      } else if (sortField === 'checkInStatus') {
        const statusOrder = { completed: 2, in_progress: 1, pending: 0 };
        aValue = statusOrder[a.checkInStatus] || 0;
        bValue = statusOrder[b.checkInStatus] || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleRowExpansion = (guestId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(guestId)) {
      newExpanded.delete(guestId);
    } else {
      newExpanded.add(guestId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSendReminder = async (guestId?: string) => {
    try {
      await sendReminderMutation.mutateAsync({
        bookingId: booking.bookingId,
        guestId,
      });
    } catch (error) {
      console.error('Failed to send reminder:', error);
    }
  };

  const handleExportData = async (format: 'csv' | 'pdf') => {
    try {
      const result = await exportDataMutation.mutateAsync({
        bookingId: booking.bookingId,
        format,
      });

      // Open download URL in new tab
      window.open(result.downloadUrl, '_blank');
    } catch (error) {
      console.error('Failed to export data:', error);
    }
  };

  const handleViewDocument = (documentId: string, documentType: 'id_front' | 'id_back', guestName?: string) => {
    setSelectedDocumentId(documentId);
    setSelectedDocumentType(documentType);
    setSelectedGuestName(guestName || '');
    setDocumentViewerOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'pending':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle fontSize="small" />;
      case 'in_progress':
        return <PendingActions fontSize="small" />;
      case 'pending':
        return <PendingActions fontSize="small" />;
      default:
        return <PendingActions fontSize="small" />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading guest details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load guest details. Please try again.
      </Alert>
    );
  }

  if (hasAccessDenied) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        You don't have permission to view guest details for this booking.
      </Alert>
    );
  }

  if (guests.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Guests Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No guests have completed check-in for this booking yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header with booking info */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Guest Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {listingTitle} • {new Date(booking.startDate).toLocaleDateString()} - {new Date(booking.endDate).toLocaleDateString()}
        </Typography>
      </Box>

      {/* Search and Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          startIcon={<Send />}
          onClick={() => handleSendReminder()}
          disabled={sendReminderMutation.isLoading}
          size="small"
        >
          Send Reminder to All
        </Button>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={() => handleExportData('csv')}
          disabled={exportDataMutation.isLoading}
          size="small"
        >
          Export CSV
        </Button>
      </Box>

      {/* Guest Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40}></TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'guestName'}
                  direction={sortField === 'guestName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('guestName')}
                >
                  Guest Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Contact Info</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'checkInStatus'}
                  direction={sortField === 'checkInStatus' ? sortDirection : 'asc'}
                  onClick={() => handleSort('checkInStatus')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'documentsVerified'}
                  direction={sortField === 'documentsVerified' ? sortDirection : 'asc'}
                  onClick={() => handleSort('documentsVerified')}
                >
                  Documents
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'completedAt'}
                  direction={sortField === 'completedAt' ? sortDirection : 'asc'}
                  onClick={() => handleSort('completedAt')}
                >
                  Completed At
                </TableSortLabel>
              </TableCell>
              <TableCell width={120}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGuests.map((guest) => (
              <React.Fragment key={guest.guestId || guest.guestName}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(guest.guestId || guest.guestName || '')}
                    >
                      {expandedRows.has(guest.guestId || guest.guestName || '') ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <Person fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" fontWeight="medium">
                        {guest.guestName || 'Unknown Guest'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {guest.contactInfo?.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2" fontSize="0.75rem">
                            {guest.contactInfo.email}
                          </Typography>
                        </Box>
                      )}
                      {guest.contactInfo?.whatsappNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <WhatsApp fontSize="small" color="action" />
                          <Typography variant="body2" fontSize="0.75rem">
                            {guest.contactInfo.whatsappNumber}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(guest.checkInStatus)}
                      label={guest.checkInStatus.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(guest.checkInStatus) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={guest.documentsVerified ? 'Verified' : 'Pending'}
                      color={guest.documentsVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize="0.75rem">
                      {guest.completedAt
                        ? new Date(guest.completedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                        : '-'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleSendReminder(guest.guestId)}
                        disabled={sendReminderMutation.isLoading}
                        title="Send Reminder"
                      >
                        <Send fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="View Details"
                        onClick={() => toggleRowExpansion(guest.guestId || guest.guestName || '')}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Expanded Row Content */}
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0 }}>
                    <Collapse
                      in={expandedRows.has(guest.guestId || guest.guestName || '')}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Detailed Information
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {/* Contact Details */}
                          <Box sx={{ minWidth: 200 }}>
                            <Typography variant="body2" fontWeight="medium" gutterBottom>
                              Contact Information
                            </Typography>
                            <List dense>
                              {guest.contactInfo?.email && (
                                <ListItem disablePadding>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Email fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={guest.contactInfo.email}
                                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                                  />
                                </ListItem>
                              )}
                              {guest.contactInfo?.whatsappNumber && (
                                <ListItem disablePadding>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <WhatsApp fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={guest.contactInfo.whatsappNumber}
                                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                                  />
                                </ListItem>
                              )}
                            </List>
                          </Box>

                          {/* Optional Information */}
                          {guest.checkInRecord?.optionalInfo && (
                            <Box sx={{ minWidth: 200 }}>
                              <Typography variant="body2" fontWeight="medium" gutterBottom>
                                Additional Information
                              </Typography>
                              <List dense>
                                {guest.checkInRecord.optionalInfo.instagramId && (
                                  <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <Instagram fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={`@${guest.checkInRecord.optionalInfo.instagramId}`}
                                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                                    />
                                  </ListItem>
                                )}
                                {guest.checkInRecord.optionalInfo.arrivalTime && (
                                  <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <Schedule fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={`Arrival: ${guest.checkInRecord.optionalInfo.arrivalTime}`}
                                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                                    />
                                  </ListItem>
                                )}
                                {guest.checkInRecord.optionalInfo.dietaryRestrictions && (
                                  <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <Restaurant fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={guest.checkInRecord.optionalInfo.dietaryRestrictions}
                                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                                    />
                                  </ListItem>
                                )}
                                {guest.checkInRecord.optionalInfo.emergencyContact?.name && (
                                  <ListItem disablePadding>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <ContactEmergency fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={`${guest.checkInRecord.optionalInfo.emergencyContact.name} (${guest.checkInRecord.optionalInfo.emergencyContact.relationship})`}
                                      secondary={guest.checkInRecord.optionalInfo.emergencyContact.phone}
                                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                    />
                                  </ListItem>
                                )}
                              </List>
                            </Box>
                          )}

                          {/* Document Previews */}
                          {guest.documentPreviews && (
                            <Box sx={{ minWidth: 200 }}>
                              <Typography variant="body2" fontWeight="medium" gutterBottom>
                                ID Documents
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {guest.documentPreviews.idFront && (
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Front ID
                                    </Typography>
                                    <DocumentThumbnail
                                      documentId={guest.documentPreviews.idFront}
                                      documentType="id_front"
                                      onView={() => handleViewDocument(
                                        guest.documentPreviews!.idFront!,
                                        'id_front',
                                        guest.guestName
                                      )}
                                      size="small"
                                    />
                                  </Box>
                                )}
                                {guest.documentPreviews.idBack && (
                                  <Box>
                                    <Typography variant="caption" display="block">
                                      Back ID
                                    </Typography>
                                    <DocumentThumbnail
                                      documentId={guest.documentPreviews.idBack}
                                      documentType="id_back"
                                      onView={() => handleViewDocument(
                                        guest.documentPreviews!.idBack!,
                                        'id_back',
                                        guest.guestName
                                      )}
                                      size="small"
                                    />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Document Viewer Modal */}
      {selectedDocumentId && (
        <DocumentViewerModal
          open={documentViewerOpen}
          onClose={() => setDocumentViewerOpen(false)}
          documentId={selectedDocumentId}
          documentType={selectedDocumentType}
          guestName={selectedGuestName}
        />
      )}
    </Box>
  );
};